import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    getStoredPassword,
    clearStoredPassword,
    adminFetchOrders,
    adminSendChat,
    adminFetchReviews,
    adminApproveReview,
    adminDeleteReview,
} from "../lib/admin";
import { Loader2, LogOut, ShoppingBag, MessageSquare, Send, RefreshCw, Star, Check, Trash2 } from "lucide-react";
import { toast } from "sonner";

const TABS = [
    { id: "chat", label: "Assistant", icon: MessageSquare },
    { id: "orders", label: "Orders", icon: ShoppingBag },
    { id: "reviews", label: "Reviews", icon: Star },
];

function OrdersTab() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = () => {
        setLoading(true);
        adminFetchOrders().then(setOrders).catch(() => setOrders([])).finally(() => setLoading(false));
    };

    useEffect(() => {
        load();
    }, []);

    return (
        <div data-testid="orders-tab" className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <div className="text-overline text-[#A3A3A3]">Stripe — Recent Activity</div>
                    <div className="text-display text-2xl mt-1">Orders.</div>
                </div>
                <button onClick={load} data-testid="orders-refresh" className="btn-ghost text-xs">
                    <RefreshCw className="h-3.5 w-3.5" strokeWidth={1.4} /> Refresh
                </button>
            </div>

            {loading && (
                <div className="text-center py-16">
                    <Loader2 className="h-6 w-6 mx-auto animate-spin text-[#A3A3A3]" strokeWidth={1.2} />
                </div>
            )}
            {!loading && orders.length === 0 && (
                <div className="border border-white/10 p-10 text-center">
                    <div className="text-overline text-[#666] mb-2">No orders yet</div>
                    <p className="text-[#A3A3A3] text-sm">When customers complete checkout, they'll appear here.</p>
                </div>
            )}
            {!loading && orders.length > 0 && (
                <div className="border border-white/10 divide-y divide-white/[0.06]">
                    {orders.map((o) => (
                        <div key={o.session_id} data-testid={`order-${o.session_id}`} className="p-4 sm:p-5 flex items-start gap-4 flex-wrap sm:flex-nowrap">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-3 flex-wrap">
                                    <span className={`px-2 py-0.5 text-[10px] tracking-[0.18em] uppercase font-display border ${
                                        o.payment_status === "paid"
                                            ? "border-[#3B6E3E] text-[#9DE0A0]"
                                            : "border-white/15 text-[#A3A3A3]"
                                    }`}>
                                        {o.payment_status}
                                    </span>
                                    <span className="text-overline text-[#666]">
                                        {o.created_at ? new Date(o.created_at).toLocaleString() : "—"}
                                    </span>
                                </div>
                                <div className="text-display text-lg mt-2 break-all">
                                    {o.customer_email || <span className="text-[#666]">no email</span>}
                                </div>
                                <ul className="mt-2 text-xs text-[#A3A3A3] space-y-0.5">
                                    {o.items?.slice(0, 6).map((it, i) => (
                                        <li key={`${o.session_id}-${i}`}>
                                            · {it.name} <span className="text-[#666]">— size {it.size} · qty {it.qty}</span>
                                        </li>
                                    ))}
                                </ul>
                                <div className="text-[10px] text-[#666] mt-2 font-mono break-all">{o.session_id}</div>
                            </div>
                            <div className="text-right shrink-0">
                                <div className="text-display text-2xl">${o.amount_total?.toFixed(0)}</div>
                                <div className="text-overline text-[#666]">{o.currency?.toUpperCase()}</div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

const STARTER_PROMPTS = [
    "Draft a 4-line journal post about the Studio N° 037 fragrance launch.",
    "Write a WhatsApp reply to a customer asking if the wide-leg trousers run true to size.",
    "Suggest 5 Instagram caption options for a Nassau beach shoot featuring the Onyx One-Piece.",
    "Brainstorm 3 names for a future cashmere overcoat in storm-grey for FW27.",
];

function ChatTab() {
    const [messages, setMessages] = useState([
        {
            role: "assistant",
            text: "I'm your SAVNT brand assistant. Ask me to draft journal posts, product descriptions, customer DMs, or campaign concepts — all in the SAVNT voice. What do you want to build?",
        },
    ]);
    const [input, setInput] = useState("");
    const [sending, setSending] = useState(false);
    const [sessionId, setSessionId] = useState(null);
    const scrollRef = useRef(null);

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages, sending]);

    const send = async (text) => {
        const msg = (text ?? input).trim();
        if (!msg || sending) return;
        setInput("");
        setMessages((m) => [...m, { role: "user", text: msg }]);
        setSending(true);
        try {
            const res = await adminSendChat(msg, sessionId);
            setSessionId(res.session_id);
            setMessages((m) => [...m, { role: "assistant", text: res.reply }]);
        } catch (e) {
            setMessages((m) => [...m, { role: "assistant", text: "Something went wrong reaching the assistant. Try again." }]);
        } finally {
            setSending(false);
        }
    };

    return (
        <div data-testid="chat-tab" className="flex flex-col h-[70vh] border border-white/10">
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 sm:p-6 space-y-5">
                {messages.map((m, i) => (
                    <div key={`msg-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                            m.role === "user"
                                ? "bg-[#EDEDE6] text-[#0C0C0C] font-display"
                                : "border border-white/10 text-[#D6D6CC]"
                        }`}>
                            {m.text}
                        </div>
                    </div>
                ))}
                {sending && (
                    <div className="flex justify-start">
                        <div className="border border-white/10 px-4 py-3 text-sm text-[#A3A3A3] inline-flex items-center gap-2">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" /> drafting…
                        </div>
                    </div>
                )}
            </div>

            {messages.length <= 1 && (
                <div className="border-t border-white/10 px-5 sm:px-6 py-3 flex flex-wrap gap-2">
                    {STARTER_PROMPTS.map((p) => (
                        <button
                            key={p}
                            onClick={() => send(p)}
                            data-testid="starter-prompt"
                            className="text-[11px] text-[#A3A3A3] hover:text-[#F4F4F0] border border-white/10 hover:border-white/30 px-3 py-1.5 transition-colors"
                        >
                            {p}
                        </button>
                    ))}
                </div>
            )}

            <form
                onSubmit={(e) => { e.preventDefault(); send(); }}
                className="border-t border-white/10 p-3 sm:p-4 flex items-end gap-2"
            >
                <textarea
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            send();
                        }
                    }}
                    rows={2}
                    placeholder="Tell me what to draft…"
                    data-testid="chat-input"
                    className="flex-1 bg-transparent border border-white/10 px-3 py-2 text-sm text-[#F4F4F0] placeholder:text-[#666] focus:outline-none focus:border-white/40 resize-none"
                />
                <button
                    type="submit"
                    disabled={sending || !input.trim()}
                    data-testid="chat-send"
                    className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    <Send className="h-4 w-4" />
                </button>
            </form>
        </div>
    );
}

function ReviewsTab() {
    const [status, setStatus] = useState("pending");
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = (s = status) => {
        setLoading(true);
        adminFetchReviews(s).then(setReviews).catch(() => setReviews([])).finally(() => setLoading(false));
    };

    useEffect(() => {
        load(status);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status]);

    const onApprove = async (id) => {
        try {
            await adminApproveReview(id);
            setReviews((prev) => prev.filter((r) => r.id !== id));
            toast.success("Review approved and live.");
        } catch (e) {
            toast.error("Could not approve.");
        }
    };

    const onDelete = async (id) => {
        if (!window.confirm("Delete this review permanently?")) return;
        try {
            await adminDeleteReview(id);
            setReviews((prev) => prev.filter((r) => r.id !== id));
            toast.success("Review deleted.");
        } catch (e) {
            toast.error("Could not delete.");
        }
    };

    return (
        <div data-testid="reviews-tab" className="space-y-6">
            <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <div className="text-overline text-[#A3A3A3]">Customer Feedback</div>
                    <div className="text-display text-2xl mt-1">Reviews.</div>
                </div>
                <div className="flex items-center gap-2">
                    {["pending", "approved", "all"].map((s) => (
                        <button
                            key={s}
                            onClick={() => setStatus(s)}
                            data-testid={`reviews-filter-${s}`}
                            className={`btn-ghost text-xs ${status === s ? "text-white border-white/40" : ""}`}
                        >
                            {s.charAt(0).toUpperCase() + s.slice(1)}
                        </button>
                    ))}
                    <button onClick={() => load(status)} data-testid="reviews-refresh" className="btn-ghost text-xs">
                        <RefreshCw className="h-3.5 w-3.5" /> Refresh
                    </button>
                </div>
            </div>

            {loading && (
                <div className="flex items-center gap-2 text-overline text-[#A3A3A3]">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
                </div>
            )}

            {!loading && reviews.length === 0 && (
                <div className="border border-dashed border-white/10 py-16 text-center">
                    <div className="text-overline text-[#666] mb-2">No reviews in this view</div>
                </div>
            )}

            {!loading && reviews.length > 0 && (
                <ul className="space-y-4">
                    {reviews.map((r) => (
                        <li key={r.id} data-testid={`admin-review-${r.id}`} className="border border-white/10 p-5">
                            <div className="flex items-start justify-between gap-4 flex-wrap">
                                <div className="space-y-1 min-w-0">
                                    <div className="flex items-center gap-3 flex-wrap">
                                        <span className="text-brutal text-sm">{r.title}</span>
                                        <span className="text-overline text-[#A3A3A3]">{"★".repeat(r.rating)}{"☆".repeat(5 - r.rating)}</span>
                                        {r.verified && (
                                            <span className="text-overline text-[#3A5C45]">· Verified Buyer</span>
                                        )}
                                        {r.approved ? (
                                            <span className="text-overline text-[#3A5C45]">· Live</span>
                                        ) : (
                                            <span className="text-overline text-[#B8825A]">· Pending</span>
                                        )}
                                    </div>
                                    <div className="text-overline text-[#666]">
                                        {r.name} {r.email && <span>· {r.email}</span>} · {r.product_slug}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    {!r.approved && (
                                        <button
                                            onClick={() => onApprove(r.id)}
                                            data-testid={`review-approve-${r.id}`}
                                            className="btn-primary text-xs"
                                        >
                                            <Check className="h-3.5 w-3.5" /> Approve
                                        </button>
                                    )}
                                    <button
                                        onClick={() => onDelete(r.id)}
                                        data-testid={`review-delete-${r.id}`}
                                        className="btn-ghost text-xs"
                                    >
                                        <Trash2 className="h-3.5 w-3.5" /> Delete
                                    </button>
                                </div>
                            </div>
                            <p className="text-[#D6D6CC] leading-relaxed mt-3 text-[14px]">{r.body}</p>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}


export default function AdminDashboard() {
    const navigate = useNavigate();
    const [active, setActive] = useState("chat");

    useEffect(() => {
        if (!getStoredPassword()) navigate("/admin/login");
    }, [navigate]);

    const logout = () => {
        clearStoredPassword();
        navigate("/admin/login");
    };

    return (
        <div data-testid="admin-dashboard" className="min-h-screen bg-[#0A0A0A] text-[#F4F4F0]">
            <header className="border-b border-white/10">
                <div className="container-rail flex items-center justify-between py-5">
                    <div>
                        <div className="text-overline text-[#A3A3A3]">SAVNT — Studio</div>
                        <div className="text-display text-2xl mt-0.5">Admin.</div>
                    </div>
                    <button onClick={logout} data-testid="admin-logout" className="btn-ghost text-xs">
                        <LogOut className="h-3.5 w-3.5" strokeWidth={1.4} /> Sign out
                    </button>
                </div>
                <div className="container-rail flex gap-1 pb-0">
                    {TABS.map((t) => {
                        const Icon = t.icon;
                        const isActive = active === t.id;
                        return (
                            <button
                                key={t.id}
                                onClick={() => setActive(t.id)}
                                data-testid={`tab-${t.id}`}
                                className={`px-4 py-2.5 text-[10px] tracking-[0.22em] uppercase font-display flex items-center gap-2 border-b-2 -mb-px transition-colors ${
                                    isActive
                                        ? "border-[#EDEDE6] text-[#F4F4F0]"
                                        : "border-transparent text-[#A3A3A3] hover:text-[#F4F4F0]"
                                }`}
                            >
                                <Icon className="h-3.5 w-3.5" strokeWidth={1.4} /> {t.label}
                            </button>
                        );
                    })}
                </div>
            </header>

            <main className="container-rail py-8 sm:py-10">
                {active === "orders" && <OrdersTab />}
                {active === "chat" && <ChatTab />}
                {active === "reviews" && <ReviewsTab />}
            </main>
        </div>
    );
}
